import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { LEGAL_VERSIONS } from "@/lib/legal-versions";

const acceptInput = z.object({
  role: z.enum(["bartender", "venue", "admin"]),
  context: z.enum(["signup", "reaccept", "contractor_ack"]).default("signup"),
});

function clientIp(): string | null {
  const fwd = getRequestHeader("x-forwarded-for");
  if (fwd) return fwd.split(",")[0]!.trim();
  return getRequestHeader("cf-connecting-ip") ?? getRequestHeader("x-real-ip") ?? null;
}

function hashIp(ip: string | null): string | null {
  if (!ip) return null;
  const salt = process.env.IP_HASH_SALT ?? "";
  if (!salt) return null;
  return createHash("sha256").update(`${ip}|${salt}`).digest("hex");
}

/**
 * Record a timestamped Terms-of-Service acceptance for the current user.
 * Called by the sign-up flow (every role) and by re-acceptance prompts after
 * a version bump. Append-only: every acceptance becomes a new row.
 */
export const recordTosAcceptance = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => acceptInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const ua = (getRequestHeader("user-agent") ?? "").slice(0, 512) || null;
    const ip_hash = hashIp(clientIp());

    const { error } = await supabase.from("tos_acceptances").insert({
      user_id: userId,
      role: data.role,
      terms_version: LEGAL_VERSIONS.terms,
      privacy_version: LEGAL_VERSIONS.privacy,
      contractor_terms_version:
        data.role === "bartender" ? LEGAL_VERSIONS.contractorAgreement : null,
      ip_hash,
      user_agent: ua,
      context: data.context,
    });
    if (error) {
      console.error("[tos] insert failed", error);
      return { ok: false as const, error: error.message };
    }
    return { ok: true as const, versions: LEGAL_VERSIONS };
  });

/** Returns the signed-in user's most recent acceptance row, if any. */
export const getLatestTosAcceptance = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("tos_acceptances")
      .select("*")
      .eq("user_id", userId)
      .order("accepted_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return { acceptance: data ?? null, currentVersions: LEGAL_VERSIONS };
  });
